import React, { memo, useCallback } from "react";
import { Info, MessageCircle, PieChart } from "lucide-react";
import dynamic from "next/dynamic";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { GameEntityDTO, ICommentDTO, ImageDTO } from "@/types";
import MetadataSection from "../MetadataSection";
interface TabSectionProps {
  stats: GameEntityDTO | null;
  comments: ICommentDTO[];
  selectedImage: ImageDTO;
}

// Simpler loading states to reduce initial paint time
const simpleSkeleton = <Skeleton className="w-full min-h-[200px] animate-pulse bg-slate-800/40" />;

// Optimized dynamic imports
const CommentSection = dynamic(() => import("components/molecules/CommentSection"), {
  ssr: false,
  loading: () => simpleSkeleton
});
const StatsSection = dynamic(() => import("components/molecules/StatsSection"), {
  ssr: false,
  loading: () => simpleSkeleton
});

// Memoized component to prevent unnecessary re-renders
const TabSection = memo(({
  stats,
  comments,
  selectedImage
}: TabSectionProps) => {
  const renderComments = useCallback(() => <CommentSection image={selectedImage} initialComments={comments} />, [selectedImage, comments]);
  const renderStats = useCallback(() => stats ? <StatsSection image={selectedImage} statsProps={stats} /> : null, [selectedImage, stats]);
  return <Tabs defaultValue="metadata" className="w-full">
                <TabsList className="grid grid-cols-3 !mb-6 bg-slate-800">
                    <TabsTrigger value="metadata" className="data-[state=active]:!bg-sky-700 data-[state=active]:text-white" aria-label="Image details tab">
                        <Info size={16} className="mr-2" />
                        <span className="xs:inline text-xs">Details</span>
                    </TabsTrigger>
                    <TabsTrigger value="stats" disabled={!stats} className="data-[state=active]:!bg-sky-700 data-[state=active]:text-white" aria-label="Stats tab">
                        <PieChart size={16} className="mr-2" />
                        <span className="xs:inline text-xs">Stats</span>
                    </TabsTrigger>
                    <TabsTrigger value="comments" className="data-[state=active]:!bg-sky-700 data-[state=active]:text-white" aria-label="Comments tab">
                        <MessageCircle size={16} className="mr-2" />
                        <span className="xs:inline text-xs">Comments</span>
                        {comments.length > 0 && <Badge variant="outline" className="ml-2 bg-slate-medium" aria-label={`${comments.length} comments`}>
                                {comments.length}
                            </Badge>}
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="metadata" className="animate-fade-in">
                    <Card className="glass-card">
                        <MetadataSection imageInfo={selectedImage} />
                    </Card>
                </TabsContent>

                <TabsContent value="stats" className="animate-fade-in">
                    <Card className="glass-card">{renderStats()}</Card>
                </TabsContent>

                <TabsContent value="comments" className="animate-fade-in">
                    <Card className="glass-card">{renderComments()}</Card>
                </TabsContent>
            </Tabs>;
});

// Add display name to improve debugging
TabSection.displayName = "ImageTabs";
export default TabSection;